import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Alert, Button, Empty, Input, Radio, Spin, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import CmsTopBar from "components/cmslayout/CmsTopBar";
import PackageCard from "./components/PackageCard";
import PackageFormModal from "./components/PackageFormModal";
import PackageDetailModal from "./components/PackageDetailModal";
import DeletePackageModal from "./components/DeletePackageModal";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  clearPackagesError,
  createPackage,
  deletePackage,
  fetchPackages,
  updatePackage,
} from "store/slices/packagesSlice";
import type {
  FetchPackagesParams,
  PackageFormValues,
  PackagePayload,
  PackageStatusFilter,
} from "./types";

interface PackagesPageProps {}

const PACKAGE_CURRENCY = "NGN";

const getFilterParams = (filter: PackageStatusFilter) => {
  if (filter === "active" || filter === "inactive") {
    return { status: filter };
  }

  if (filter === "weekly" || filter === "monthly") {
    return { billing_cycle: filter };
  }

  return {};
};

const buildPayload = (values: PackageFormValues): PackagePayload => ({
  image: values.image || "",
  name: values.name.trim(),
  billing_cycle: values.billing_cycle,
  currency: PACKAGE_CURRENCY,
  description: values.description.trim(),
  benefits: values.benefits.map((benefit) => benefit.trim()).filter(Boolean),
  is_active: values.is_active,
  items: values.items.map((item) => ({
    master_product_id: Number(item.master_product_id),
    quantity: Number(item.quantity),
    unit: item.unit,
    is_required: Boolean(item.is_required),
  })),
});

export const PackagesPage: React.FunctionComponent<PackagesPageProps> = () => {
  const dispatch = useAppDispatch();
  const { actionLoading, error, loading, packages } = useAppSelector(
    (state) => state.packages
  );
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filter, setFilter] = useState<PackageStatusFilter>("all");

  const queryParams = useMemo<FetchPackagesParams>(
    () => ({
      search: debouncedSearch,
      ...getFilterParams(filter),
    }),
    [debouncedSearch, filter]
  );

  const editingPackage = useMemo(
    () => packages.find((item) => item.id === editingId) ?? null,
    [packages, editingId]
  );

  const viewingPackage = useMemo(
    () => packages.find((item) => item.id === viewingId) ?? null,
    [packages, viewingId]
  );

  const deletingPackage = useMemo(
    () => packages.find((item) => item.id === deletingId) ?? null,
    [packages, deletingId]
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);

    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    dispatch(fetchPackages(queryParams));
  }, [dispatch, queryParams]);

  const refetchPackages = () => {
    dispatch(fetchPackages(queryParams));
  };

  const openAdd = () => {
    dispatch(clearPackagesError());
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (id: number) => {
    dispatch(clearPackagesError());
    setEditingId(id);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (actionLoading) return;
    setFormOpen(false);
    setEditingId(null);
  };

  const openView = (id: number) => {
    setViewingId(id);
    setDetailOpen(true);
  };

  const closeView = () => {
    setDetailOpen(false);
    setViewingId(null);
  };

  const requestDelete = (id: number) => {
    dispatch(clearPackagesError());
    setDeletingId(id);
  };

  const closeDelete = () => {
    if (actionLoading) return;
    setDeletingId(null);
  };

  const savePackage = async (values: PackageFormValues) => {
    const payload = buildPayload(values);

    if (process.env.NODE_ENV === "development") {
      console.log("[PACKAGE SUBMIT] currency", payload.currency);
    }

    if (editingId && editingPackage) {
      const result = await dispatch(
        updatePackage({ id: editingId, payload })
      );

      if (!updatePackage.fulfilled.match(result)) {
        message.error(result.payload || "Unable to update package.");
        return;
      }

      setFormOpen(false);
      setEditingId(null);
      refetchPackages();
      message.success("Package updated.");
      return;
    }

    const result = await dispatch(createPackage(payload));

    if (!createPackage.fulfilled.match(result)) {
      message.error(result.payload || "Unable to create package.");
      return;
    }

    setFormOpen(false);
    refetchPackages();
    message.success("Package added.");
  };

  const confirmDelete = async () => {
    if (!deletingId) return;

    const name = deletingPackage?.name;
    const result = await dispatch(deletePackage(deletingId));

    if (!deletePackage.fulfilled.match(result)) {
      message.error(result.payload || "Unable to delete package.");
      return;
    }

    if (viewingId === deletingId) {
      closeView();
    }

    setDeletingId(null);
    refetchPackages();
    message.success(`Package${name ? ` (${name})` : ""} deleted.`);
  };

  return (
    <React.Fragment>
      <CmsTopBar title="Packages" />

      <Container fluid className="dashboard-wrap">
        <Container fluid className="px-3 py-4">
          <div className="markets-section-header mb-3">
            <h2 className="markets-heading">
              Subscription Packages ({packages.length})
            </h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="markets-add-btn"
              onClick={openAdd}
            >
              Add Package
            </Button>
          </div>

          <Row className="mb-3 gy-2">
            <Col xs={12} lg={7}>
              <Input
                placeholder="Search packages"
                prefix={<SearchOutlined className="markets-search-icon" />}
                className="markets-search markets-search-full"
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Col>
            <Col xs={12} lg={5}>
              <Radio.Group
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                className="package-filter-group"
              >
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="active">Active</Radio.Button>
                <Radio.Button value="inactive">Inactive</Radio.Button>
                <Radio.Button value="weekly">Weekly</Radio.Button>
                <Radio.Button value="monthly">Monthly</Radio.Button>
              </Radio.Group>
            </Col>
          </Row>

          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              closable
              className="mb-3"
              onClose={() => dispatch(clearPackagesError())}
            />
          )}

          <div
            className={`package-list-wrap ${
              !loading && packages.length === 0 ? "package-list-wrap-empty" : ""
            }`}
          >
            {loading ? (
              <div className="package-loading-state">
                <Spin size="large" />
              </div>
            ) : packages.length === 0 ? (
              <Empty
                description={
                  <div style={{ textAlign: "center", marginTop: 12 }}>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: "#1f1f1f",
                      }}
                    >
                      No packages found.
                    </div>
                  </div>
                }
              />
            ) : (
              <div className="packages-card-grid">
                {packages.map((item) => (
                  <PackageCard
                    key={item.id}
                    groceryPackage={item}
                    onView={openView}
                    onEdit={openEdit}
                    onDeleteRequest={requestDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </Container>
      </Container>

      <PackageFormModal
        open={formOpen}
        mode={editingId ? "edit" : "add"}
        initialPackage={editingPackage}
        loading={actionLoading}
        onClose={closeForm}
        onSubmit={savePackage}
      />

      <PackageDetailModal
        open={detailOpen}
        groceryPackage={viewingPackage}
        onClose={closeView}
      />

      <DeletePackageModal
        open={!!deletingId}
        packageName={deletingPackage?.name}
        loading={actionLoading}
        onClose={closeDelete}
        onConfirm={confirmDelete}
      />
    </React.Fragment>
  );
};

export default PackagesPage;
